import React from 'react';
import { Typography } from '@mui/material';
import _, { omit } from 'lodash';

import { Table } from '@molecules';
import { MidnightQueries, MonitoringItem, Queries } from '@services';
import { getColumns } from './utils';
import QueryKeys from '@constants/query-keys';
import { useParams } from 'react-router-dom';
import { AddMonitoringModal } from 'src/features/Enterprise/Monitoring/AddMonitoringModal/AddMonitoringModal';
import { useMonitoring } from 'src/features/Enterprise/Monitoring/useMonitoring';
import { useModalState } from '@hooks';
import { ConfirmModal } from '@organisms/Modals';
import { DeleteModalIcon } from '@atoms/Icons/DeleteModalIcon';

const MonitoringTable = () => {
  const { id: organizationId } = useParams<{ id: string }>();

  const [isConfirmDelete, , openConfirmDeleteModal, closeConfirmDeleteModal] = useModalState(false);

  const {
    isOpenModal,
    defaultItem,
    editingItem,
    openModal,
    closeModal,
    setEditingItem,
    onEdit,
    onDelete,
  } = useMonitoring();

  const _formatData = (data?: MonitoringItem[]) => {
    if (!data) {
      return { items: [], totalCount: 0 };
    }

    // TODO: temporarily hide pagination until backend is ready
    return { items: data, totalCount: 0 };
  };

  const onClickEdit = (item: MonitoringItem) => {
    onEdit(item);
    openModal();
  };

  const onClickDelete = (item: MonitoringItem) => {
    setEditingItem(item);
    openConfirmDeleteModal();
  };

  const onClose = () => {
    setEditingItem(defaultItem);
    closeModal();
  };

  const onCancel = () => {
    setEditingItem(defaultItem);
    closeConfirmDeleteModal();
  };

  const onSubmit = () => {
    onDelete(editingItem);
    closeConfirmDeleteModal();
  };

  const formatQueriesUsingOrderParams = (queries: Queries | MidnightQueries) => {
    const formattedQueries = Object.entries({
      ...omit(queries, ['sortDirection', 'sortBy', 'sortOrder']),
    }).reduce((acc, [key, value]) => {
      if (value !== '' && !_.isNil(value)) {
        let formattedValue = value;
        if (key === 'pageSize') {
          formattedValue = String(value);
        }
        return { ...acc, [key]: formattedValue };
      }
      return acc;
    }, {});

    const sortSymbol = (queries as Queries).sortDirection?.toLowerCase() === 'asc' ? '' : '-';

    const order = `${sortSymbol}${queries.sortBy}`;

    return { ...formattedQueries, order };
  };

  return (
    <>
      <Table<MonitoringItem>
        searchPlaceholder="Search for Asset's titles"
        columns={getColumns({ onClickEdit, onClickDelete })}
        queryName={QueryKeys.enterpriseMonitoring}
        isEnterprise
        apiRoute={`/enterprises-admin/${organizationId}/assets`}
        formatData={_formatData}
        customizeQueries={formatQueriesUsingOrderParams}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        usingIsFetching
        checkHidePagination
      />
      <AddMonitoringModal
        open={isOpenModal}
        onClose={onClose}
        title="Edit asset item"
        subtitle="Here you can edit new asset item"
        editingItem={editingItem}
      />
      <ConfirmModal
        open={isConfirmDelete}
        onCancel={onCancel}
        onSubmit={onSubmit}
        icon={<DeleteModalIcon width={55} height={71} />}
        title={
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F6080',
              whiteSpace: 'normal',
              marginBottom: '-8px',
            }}
          >
            Are you sure you want to delete this item?
          </Typography>
        }
        submitText="Delete"
        submitColor="error"
        usingModalV2
      />
    </>
  );
};

export default MonitoringTable;
